import { useCallback, useEffect, useState } from 'react';
import { CircleCheck, CircleX, Clock, Compass, Lock } from 'lucide-react';
import type { CommunityCategory, SpaceDiscoveryInfo } from '@spawnwire/shared';
import { Button, clsx, Field, Spinner, TextArea } from '../../components/ui';
import { errorMessage, http } from '../../lib/api';
import { attempt } from '../../lib/data';
import { formatTimestamp } from '../../lib/time';
import { CATEGORIES, CATEGORY_META } from '../../lib/discover';
import { useStore } from '../../store/store';
import { ui } from '../../store/ui';
import { Section } from '../settings/SettingsLayout';

function CategoryPicker({ value, onChange, disabled }: { value: CommunityCategory | null; onChange: (c: CommunityCategory) => void; disabled?: boolean }) {
  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {CATEGORIES.map((c) => {
        const { icon: Icon, label, color } = CATEGORY_META[c];
        return (
          <button
            key={c}
            type="button"
            disabled={disabled}
            aria-pressed={value === c}
            onClick={() => onChange(c)}
            className={clsx(
              'flex items-center gap-2 rounded-lg border px-3 py-2 text-left text-sm font-medium transition-colors disabled:opacity-60',
              value === c ? 'border-accent bg-accent/10 text-fg' : 'border-line bg-sidebar text-muted hover:bg-elevated hover:text-fg',
            )}
          >
            <Icon className="size-4 shrink-0" style={{ color }} aria-hidden />
            {label}
          </button>
        );
      })}
    </div>
  );
}

const STATUS = {
  none: { icon: Compass, tone: 'text-muted', title: 'Not on Discover', body: 'Only people you invite can join. Apply below to let anyone find your community.' },
  pending: { icon: Clock, tone: 'text-warning', title: 'Under review', body: "Spawnwire staff are reviewing your community. It won't appear on Discover until it's approved, and we'll DM you with the decision." },
  approved: { icon: CircleCheck, tone: 'text-success', title: 'Listed on Discover', body: 'Anyone can find your community on Discover and join it.' },
  denied: { icon: CircleX, tone: 'text-danger', title: 'Not approved', body: 'Your last application was denied. Make changes and apply again whenever you are ready.' },
} as const;

/** Space settings → Discovery, for the owner: turn a friends space into a community and apply to Discover. */
export function DiscoverySettings({ spaceId }: { spaceId: string }) {
  const space = useStore((s) => s.spaces[spaceId]!);
  const [info, setInfo] = useState<SpaceDiscoveryInfo | null>(null);
  const [category, setCategory] = useState<CommunityCategory | null>(space.category);
  const [description, setDescription] = useState(space.description);
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    http
      .get<SpaceDiscoveryInfo>(`/spaces/${spaceId}/discovery`)
      .then(setInfo)
      .catch((err) => setError(errorMessage(err)));
  }, [spaceId]);

  // Reload whenever the status changes (a review lands while this is open).
  useEffect(load, [load, space.discovery]);

  async function run(fn: () => Promise<unknown>, done?: string) {
    setBusy(true);
    setError(null);
    try {
      await fn();
      if (done) ui.toast(done, 'success');
      load();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (space.kind === 'friends') {
    return (
      <Section title="Discovery" description="Friends spaces are private and never appear on Discover.">
        <div className="flex items-start gap-3 rounded-xl border border-line bg-sidebar p-4">
          <Lock className="mt-0.5 size-5 shrink-0 text-muted" aria-hidden />
          <div className="text-sm text-muted">
            <p className="font-semibold text-fg">This is a friends space</p>
            <p className="mt-1">Turn it into a community to pick a category and apply to be listed on Discover. Nothing changes for your current members.</p>
          </div>
        </div>
        <Field label="What is it about?">
          <CategoryPicker value={category} onChange={setCategory} disabled={busy} />
        </Field>
        <Button disabled={!category} loading={busy} onClick={() => void run(() => http.patch(`/spaces/${spaceId}`, { kind: 'community', category }), 'This space is now a community.')}>
          Turn into a community
        </Button>
        {error && <p className="text-sm text-danger">{error}</p>}
      </Section>
    );
  }

  if (!info) return error ? <p className="text-sm text-danger">{error}</p> : <Spinner />;

  const status = STATUS[info.status];
  const locked = info.status === 'pending' || info.status === 'approved';
  const canApply = !locked && !!category && description.trim().length >= 10;

  return (
    <>
      <Section title="Discover status">
        <div className="flex items-start gap-3 rounded-xl border border-line bg-sidebar p-4">
          <status.icon className={clsx('mt-0.5 size-6 shrink-0', status.tone)} aria-hidden />
          <div className="min-w-0 flex-1 text-sm">
            <p className={clsx('font-semibold', status.tone)}>{status.title}</p>
            <p className="mt-1 text-muted">{status.body}</p>
            {info.latest && (
              <p className="mt-2 text-xs text-faint">
                Submitted {formatTimestamp(info.latest.createdAt)}
                {info.latest.reviewedAt && ` · reviewed ${formatTimestamp(info.latest.reviewedAt)}`}
              </p>
            )}
            {info.latest?.reviewNote && (info.latest.status === 'denied' || info.latest.status === 'approved') && (
              <p className="mt-2 rounded-md bg-main px-3 py-2 text-sm text-fg">
                <span className="font-semibold">Note from Spawnwire staff:</span> {info.latest.reviewNote}
              </p>
            )}
          </div>
        </div>
        {locked &&
          (confirming ? (
            <div className="rounded-lg border border-danger/40 bg-danger-soft p-3 text-sm">
              <p className="text-fg">
                {info.status === 'approved'
                  ? 'People will no longer find your community on Discover. To come back you would need to apply and be reviewed again.'
                  : 'Your community leaves the review queue. You can apply again later.'}
              </p>
              <div className="mt-3 flex gap-2">
                <Button
                  variant="danger"
                  loading={busy}
                  onClick={() => void run(() => http.del(`/spaces/${spaceId}/discovery`), 'Done.').then(() => setConfirming(false))}
                >
                  {info.status === 'approved' ? 'Remove from Discover' : 'Withdraw'}
                </Button>
                <Button variant="ghost" onClick={() => setConfirming(false)}>
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button variant="secondary" disabled={busy} onClick={() => setConfirming(true)}>
              {info.status === 'approved' ? 'Remove from Discover' : 'Withdraw application'}
            </Button>
          ))}
      </Section>

      <Section title="Your listing" description={locked ? 'Take your community off Discover to change its category.' : 'This is what reviewers and visitors see.'}>
        <Field label="Category">
          <CategoryPicker value={category} onChange={setCategory} disabled={locked || busy} />
        </Field>
        <Field label="What is your community about?" htmlFor="disc-desc" hint="At least 10 characters. This becomes your space description.">
          <TextArea id="disc-desc" rows={3} value={description} onChange={(e) => setDescription(e.target.value)} maxLength={500} disabled={locked || busy} />
        </Field>
        {!locked && (
          <div className="flex flex-wrap gap-2">
            <Button disabled={!canApply} loading={busy} onClick={() => void run(() => http.post(`/spaces/${spaceId}/discovery`, { category, description }), 'Submitted for review. Check your DMs.')}>
              {info.status === 'denied' ? 'Apply again' : 'Submit to Discover'}
            </Button>
            <Button
              variant="ghost"
              disabled={busy}
              onClick={() => void attempt(() => http.patch(`/spaces/${spaceId}`, { kind: 'friends' }), 'This is a friends space again.')}
            >
              Make it a friends space
            </Button>
          </div>
        )}
        {error && <p className="text-sm text-danger">{error}</p>}
      </Section>
    </>
  );
}
